import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarAlt, FaPlus, FaSearch, FaTable } from "react-icons/fa";
import Layout from "../../../components/Layout";
import MonthCalendar from "../../../components/MonthCalendar";
import { crm } from "../../../theme";
import { CommercialSupplier, joinPipe, splitPipe, supplierCategories } from "../../../models/CommercialSupplier";
import {
  deleteCommercialSupplier,
  getCommercialSuppliers,
  saveCommercialSupplier,
  updateCommercialSupplier,
} from "../../../services/commercialSupplierService";

const categoryColors: Record<string, string> = {
  PCR: "blue",
  TBR: "purple",
  OTR: "orange",
  AGR: "green",
  INDUSTRIAL: "gray",
  CONSTRUCTION: "yellow",
  UTILITÁRIOS: "teal",
};

const dateInput = (value?: string | null) => (value ? value.substring(0, 10) : "");
const dateText = (value?: string | null) =>
  value ? new Date(`${value.substring(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "";

const emptySupplier: CommercialSupplier = {};

// Links de arquivos vêm de texto livre: só http(s) vira <a href> (bloqueia javascript:, data:, etc.).
const isHttpUrl = (value: string) => /^https?:\/\//i.test(value);

// Ignora campos vazios/nulos: apagar o que foi digitado volta a "sem alterações".
const snapshot = (supplier: CommercialSupplier) =>
  JSON.stringify(Object.fromEntries(Object.entries(supplier).filter(([, value]) => value !== "" && value != null)));

// Usa a grafia conhecida (ex.: "pcr" -> "PCR") para a cor e a deduplicação não dependerem de maiúsculas.
const canonical = (item: string, known?: string[]) => known?.find((name) => name.toLowerCase() === item.toLowerCase()) ?? item;

const addToList = (list: string | null | undefined, raw: string | undefined, known?: string[]) => {
  const item = canonical((raw ?? "").replace(/\|/g, " ").trim(), known);
  const items = splitPipe(list);
  if (item && !items.some((existing) => existing.toLowerCase() === item.toLowerCase())) items.push(item);
  return joinPipe(items);
};

const withPendingTags = (supplier: CommercialSupplier, pending: Record<string, string>): CommercialSupplier => ({
  ...supplier,
  origins: addToList(supplier.origins, pending.origins),
  brands: addToList(supplier.brands, pending.brands),
  categories: addToList(supplier.categories, pending.categories, supplierCategories),
});

const CommercialSuppliers = () => {
  const [suppliers, setSuppliers] = useState<CommercialSupplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<CommercialSupplier>(emptySupplier);
  const [formKey, setFormKey] = useState(0);
  const [savedName, setSavedName] = useState("");
  const [initialDraft, setInitialDraft] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingTags, setPendingTags] = useState<Record<string, string>>({});
  const drawer = useDisclosure();
  const confirmDelete = useDisclosure();
  const confirmDiscard = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSuppliers(await getCommercialSuppliers());
    } catch {
      toast({ title: "Não foi possível carregar os fornecedores.", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return suppliers;
    return suppliers.filter((supplier) => [supplier.name, supplier.origins, supplier.brands, supplier.categories, supplier.focalPoint, supplier.email, supplier.paymentTerms, supplier.notes]
      .some((field) => field?.toLowerCase().includes(value)));
  }, [suppliers, search]);

  const openSupplier = (supplier?: CommercialSupplier) => {
    const next = supplier ? { ...supplier } : emptySupplier;
    setDraft(next);
    setInitialDraft(snapshot(next));
    setSavedName(supplier?.name ?? "");
    setPendingTags({});
    setFormKey((key) => key + 1);
    drawer.onOpen();
  };

  const save = async () => {
    if (saving) return;
    if (!draft.name?.trim()) {
      toast({ title: "Informe o nome do fornecedor.", status: "warning" });
      return;
    }
    if (splitPipe(draft.fileLinks).some((link) => !isHttpUrl(link))) {
      toast({ title: "Os links de arquivos devem começar com http:// ou https://.", status: "warning" });
      return;
    }
    // Texto ainda não confirmado com Enter nas tags entra no salvamento (não depende da ordem blur/click).
    const payload = withPendingTags(draft, pendingTags);
    setSaving(true);
    try {
      const response = payload.id ? await updateCommercialSupplier(payload) : await saveCommercialSupplier(payload);
      if (response.data?.success === false) {
        toast({ title: response.data?.notifications?.[0] ?? "Não foi possível salvar o fornecedor.", status: "error" });
        return;
      }
      toast({ title: payload.id ? "Fornecedor atualizado." : "Fornecedor cadastrado.", status: "success" });
      drawer.onClose();
      await load();
    } catch {
      toast({ title: "Não foi possível salvar o fornecedor.", status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft.id || deleting) return;
    setDeleting(true);
    try {
      await deleteCommercialSupplier(draft.id);
      toast({ title: "Fornecedor excluído.", status: "success" });
      confirmDelete.onClose();
      drawer.onClose();
      await load();
    } catch {
      toast({ title: "Não foi possível excluir o fornecedor.", status: "error" });
    } finally {
      setDeleting(false);
    }
  };

  // Fechar com edição não salva pede confirmação; sem alterações fecha direto.
  const hasPendingTag = Object.values(pendingTags).some((text) => text.trim() !== "");
  const requestClose = () => (snapshot(draft) !== initialDraft || hasPendingTag ? confirmDiscard.onOpen() : drawer.onClose());
  const discardChanges = () => { confirmDiscard.onClose(); drawer.onClose(); };

  const set = (patch: Partial<CommercialSupplier>) => setDraft((current) => ({ ...current, ...patch }));
  // Containers são inteiros: descarta casas decimais e valores inválidos.
  const numberValue = (value: string) => { const parsed = Math.trunc(Number(value)); return value === "" || Number.isNaN(parsed) ? null : parsed; };

  return (
    <Box minH="100vh" bg={crm.pageBg} color={crm.text}>
      <Layout>
        <Box minH="100%" w="100%" bg={crm.pageBg} overflowY="auto" p={{ base: "18px", md: "24px" }}>
          <Box pb="36px">
            <Flex align="center" gap="12px" mb="22px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Heading fontSize="28px" color={crm.text}>Fornecedores</Heading>
            </Flex>
            <Tabs variant="unstyled" size="sm" w="100%" minW="0">
              <Flex justify="space-between" align="center" gap="14px" wrap="wrap">
                <TabList bg={crm.subtle} p="4px" borderRadius="10px" display="inline-flex" gap="2px" border="1px solid" borderColor={crm.border} flexWrap="wrap">
                  <Tab borderRadius="8px" color={crm.textMuted} _selected={{ bg: crm.surface, color: crm.brand, boxShadow: crm.shadowSm }}><FaTable style={{ marginRight: 7 }} />Tabela Geral</Tab>
                  <Tab borderRadius="8px" color={crm.textMuted} _selected={{ bg: crm.surface, color: crm.brand, boxShadow: crm.shadowSm }}><FaCalendarAlt style={{ marginRight: 7 }} />Calendário de Pedidos</Tab>
                </TabList>
                <Flex gap="9px" align="center" flexWrap="wrap">
                  <Box position="relative"><FaSearch style={{ position: "absolute", left: 10, top: 9, color: crm.textMuted, zIndex: 1 }} /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar" size="sm" w="200px" pl="31px" bg={crm.surface} borderColor={crm.borderStrong} /></Box>
                  <Button leftIcon={<FaPlus />} size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={() => openSupplier()}>Novo</Button>
                </Flex>
              </Flex>
              <TabPanels w="100%" minW="0">
                <TabPanel px="0" pt="22px" minW="0"><SupplierTable suppliers={filtered} onOpen={openSupplier} /></TabPanel>
                <TabPanel px="0" pt="22px" minW="0"><OrderCalendar suppliers={filtered} onOpen={openSupplier} /></TabPanel>
              </TabPanels>
            </Tabs>
            {loading && <Flex py="70px" justify="center"><Spinner thickness="3px" size="xl" /></Flex>}
          </Box>
        </Box>
      </Layout>

      <Drawer isOpen={drawer.isOpen} placement="right" onClose={requestClose} size="full">
        <DrawerOverlay />
        <DrawerContent bg={crm.surface} color={crm.text} w={{ base: "100%", md: "620px" }} maxW="100%" ml="auto">
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px solid" borderColor={crm.border} pr="55px">
            <Text fontSize="xs" color={crm.textMuted} mb="6px">FORNECEDOR</Text>
            <Heading size="lg">{draft.id ? draft.name : "Novo fornecedor"}</Heading>
          </DrawerHeader>
          <DrawerBody pb="24px" key={formKey}>
            <Box mt="22px">
              <Field label="Fornecedor"><Input value={draft.name ?? ""} onChange={(e) => set({ name: e.target.value })} /></Field>
              <SimpleGrid columns={2} spacing="10px" mt="10px">
                <Field label="Origem"><TagInput value={splitPipe(draft.origins)} onChange={(items) => set({ origins: joinPipe(items) })} placeholder="Ex.: China" onTextChange={(text) => setPendingTags((p) => ({ ...p, origins: text }))} /></Field>
                <Field label="Marcas"><TagInput value={splitPipe(draft.brands)} onChange={(items) => set({ brands: joinPipe(items) })} placeholder="Ex.: LINGLONG" onTextChange={(text) => setPendingTags((p) => ({ ...p, brands: text }))} /></Field>
                <Field label="Categoria"><TagInput value={splitPipe(draft.categories)} onChange={(items) => set({ categories: joinPipe(items) })} placeholder="Ex.: PCR" suggestions={supplierCategories} colors={categoryColors} onTextChange={(text) => setPendingTags((p) => ({ ...p, categories: text }))} /></Field>
                <Field label="Forma de pagamento"><Input value={draft.paymentTerms ?? ""} onChange={(e) => set({ paymentTerms: e.target.value })} /></Field>
                <Field label="Email"><Input value={draft.email ?? ""} onChange={(e) => set({ email: e.target.value })} /></Field>
                <Field label="Focal point"><Input value={draft.focalPoint ?? ""} onChange={(e) => set({ focalPoint: e.target.value })} /></Field>
                <Field label="Containers - Em água"><Input type="number" min={0} step={1} value={draft.containersInWater ?? ""} onChange={(e) => set({ containersInWater: numberValue(e.target.value) })} /></Field>
                <Field label="Containers - Em produção"><Input type="number" min={0} step={1} value={draft.containersInProduction ?? ""} onChange={(e) => set({ containersInProduction: numberValue(e.target.value) })} /></Field>
                <Field label="Tempo de produção (dias)"><Input type="number" min={0} max={730} step={1} value={draft.productionDays ?? ""} onChange={(e) => set({ productionDays: numberValue(e.target.value) })} /></Field>
                <Field label="Data do pedido"><Input type="date" value={dateInput(draft.orderDate)} onChange={(e) => set({ orderDate: e.target.value || null })} /></Field>
              </SimpleGrid>
              <Box mt="10px"><Field label="Arquivos e mídia (um link por linha)"><Textarea rows={3} defaultValue={splitPipe(draft.fileLinks).join("\n")} onChange={(e) => set({ fileLinks: joinPipe(e.target.value.split("\n").map((line) => line.trim()).filter(Boolean)) })} placeholder="https://..." /></Field></Box>
              <Box mt="10px"><Field label="Observações"><Textarea rows={4} value={draft.notes ?? ""} onChange={(e) => set({ notes: e.target.value })} /></Field></Box>
            </Box>
          </DrawerBody>
          <DrawerFooter borderTop="1px solid" borderColor={crm.border} justifyContent="space-between">
            {draft.id ? <Button variant="ghost" colorScheme="red" onClick={confirmDelete.onOpen}>Excluir</Button> : <span />}
            <Flex gap="8px"><Button variant="ghost" onClick={requestClose}>Cancelar</Button><Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} onClick={save}>Salvar</Button></Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog isOpen={confirmDelete.isOpen} leastDestructiveRef={cancelRef} onClose={confirmDelete.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir fornecedor</AlertDialogHeader>
            <AlertDialogBody>Tem certeza que deseja excluir <b>{savedName}</b>? Ele deixa de aparecer na tabela e no calendário.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={confirmDelete.onClose}>Cancelar</Button>
              <Button colorScheme="red" ml={3} isLoading={deleting} onClick={remove}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={confirmDiscard.isOpen} leastDestructiveRef={keepEditingRef} onClose={confirmDiscard.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Descartar alterações?</AlertDialogHeader>
            <AlertDialogBody>Há alterações não salvas neste fornecedor. Se fechar agora, elas serão perdidas.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={keepEditingRef} variant="ghost" onClick={confirmDiscard.onClose}>Continuar editando</Button>
              <Button colorScheme="red" ml={3} onClick={discardChanges}>Descartar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => <FormControl><FormLabel color="gray.500" fontSize="xs" mb="4px">{label}</FormLabel>{children}</FormControl>;

const TagInput = ({ value, onChange, placeholder, suggestions, colors, onTextChange }: { value: string[]; onChange: (items: string[]) => void; placeholder?: string; suggestions?: string[]; colors?: Record<string, string>; onTextChange?: (text: string) => void }) => {
  const [text, setTextState] = useState("");
  const setText = (next: string) => { setTextState(next); onTextChange?.(next); };
  const add = (raw: string) => {
    const item = canonical(raw.replace(/\|/g, " ").trim(), suggestions);
    if (item && !value.some((existing) => existing.toLowerCase() === item.toLowerCase())) onChange([...value, item]);
    setText("");
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      add(text);
    } else if (event.key === "Backspace" && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  const remaining = (suggestions ?? []).filter((item) => !value.some((existing) => existing.toLowerCase() === item.toLowerCase()));
  return <Box>
    <Flex gap="5px" wrap="wrap" mb={value.length ? "6px" : 0}>{value.map((item) => <Tag key={item} size="sm" colorScheme={colors?.[item.toUpperCase()] ?? "gray"}><TagLabel>{item}</TagLabel><TagCloseButton onClick={() => onChange(value.filter((other) => other !== item))} /></Tag>)}</Flex>
    <Input size="sm" value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} onBlur={() => add(text)} />
    {remaining.length > 0 && <Flex gap="4px" wrap="wrap" mt="6px">{remaining.map((item) => <Badge key={item} as="button" type="button" cursor="pointer" variant="outline" colorScheme={colors?.[item.toUpperCase()] ?? "gray"} onClick={() => add(item)}>+ {item}</Badge>)}</Flex>}
  </Box>;
};

const Tags = ({ items, colors }: { items?: string | null; colors?: Record<string, string> }) => <Flex gap="4px" wrap="wrap">{splitPipe(items).map((item) => <Badge key={item} colorScheme={colors?.[item.toUpperCase()] ?? "gray"} variant="subtle" fontSize="10px">{item}</Badge>)}</Flex>;

const SupplierTable = ({ suppliers, onOpen }: { suppliers: CommercialSupplier[]; onOpen: (supplier: CommercialSupplier) => void }) => (
  <Box w="100%" maxW="100%" minW="0" overflowX="auto" border="1px solid" borderColor={crm.border} borderRadius={crm.radius} bg={crm.surface} boxShadow={crm.shadowSm}>
    <Box as="table" minW="1500px" w="100%" sx={{
      borderCollapse: "collapse",
      "th, td": { borderBottom: "1px solid", borderColor: crm.border, px: "12px", py: "10px", textAlign: "left", fontSize: "sm", verticalAlign: "top" },
      th: { color: crm.textMuted, fontWeight: "600", whiteSpace: "nowrap", bg: crm.subtle },
      "tbody tr:hover": { bg: crm.brandSoft },
    }}>
      <thead><tr>{["Fornecedores", "Origem", "Marcas", "Categoria", "Arquivos e mídia", "Forma de pagamento", "Email", "Focal point", "Containers - Em água", "Containers - Em produção", "Tempo de produção", "Data"].map((header) => <th key={header}>{header}</th>)}</tr></thead>
      <tbody>{suppliers.map((supplier) => <tr key={supplier.id}>
        <td style={{ position: "sticky", left: 0, background: crm.surface, minWidth: 240 }}><Button variant="link" color={crm.brand} fontWeight="700" whiteSpace="normal" textAlign="left" onClick={() => onOpen(supplier)}>{supplier.name}</Button></td>
        <td><Tags items={supplier.origins} /></td>
        <td style={{ minWidth: 180 }}><Tags items={supplier.brands} /></td>
        <td style={{ minWidth: 200 }}><Tags items={supplier.categories} colors={categoryColors} /></td>
        <td>{splitPipe(supplier.fileLinks).map((link, index) => isHttpUrl(link)
          ? <Link key={`${index}-${link}`} href={link} isExternal rel="noopener noreferrer" color={crm.brand} display="block" fontSize="xs">Arquivo {index + 1}</Link>
          : <Text key={`${index}-${link}`} fontSize="xs" color={crm.textMuted}>{link}</Text>)}</td>
        <td>{supplier.paymentTerms ?? ""}</td>
        <td>{supplier.email ?? ""}</td>
        <td>{supplier.focalPoint ?? ""}</td>
        <td>{supplier.containersInWater ?? ""}</td>
        <td>{supplier.containersInProduction ?? ""}</td>
        <td style={{ whiteSpace: "nowrap" }}>{supplier.productionDays == null ? "" : `${supplier.productionDays} dias`}</td>
        <td style={{ whiteSpace: "nowrap" }}>{dateText(supplier.orderDate)}</td>
      </tr>)}</tbody>
    </Box>
    {suppliers.length === 0 && <Text p="30px" color={crm.textMuted} textAlign="center">Nenhum fornecedor encontrado.</Text>}
  </Box>
);

const OrderCalendar = ({ suppliers, onOpen }: { suppliers: CommercialSupplier[]; onOpen: (supplier: CommercialSupplier) => void }) => (
  <MonthCalendar
    items={suppliers}
    getDate={(supplier) => supplier.orderDate}
    getKey={(supplier) => supplier.id!}
    getLabel={(supplier) => supplier.name ?? ""}
    onOpen={onOpen}
  />
);

export default CommercialSuppliers;
